import {ColumnDef} from "@tanstack/react-table"
import {Button} from "@/components/ui/button.tsx";
import {
    CheckIcon,
    HandshakeIcon,
    HourglassIcon,
    MailIcon,
    PencilIcon,
    Printer, Trash2Icon
} from "lucide-react";

import axios from "axios";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx";

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
} from "@/components/ui/alert-dialog.tsx";
import {Link} from "react-router";
import {AuthContextProps} from "react-oidc-context";
import {Avatar, AvatarFallback} from "@/components/ui/avatar.tsx";
import {CompanyDto} from "@/types/Company";
import {Badge} from "@/components/ui/badge.tsx";
import {format} from "date-fns";
import {LockClosedIcon} from "@radix-ui/react-icons";

export type Document = {
    id: string,
    created_at: Date,
    updated_at: Date,
    title: string,
    company_name: string,
    company: CompanyDto,
    status: string,
    company_id: string,
    order: any,
    form: any,
}

const documentModules = [
    {
        id: 'lojalitas',
        name: 'Lojalitás'
    },
    {
        id: 'torzsgarda',
        name: 'Törzsgárda'
    },
    {
        id: 'szindikatus',
        name: 'Szindikátus'
    },
    {
        id: 'tokekepzes',
        name: 'Tőkeképzés'
    },
    {
        id: 'nyugdij',
        name: 'Nyugdíj'
    },
    {
        id: 'generacio',
        name: 'Generáció váltás'
    }
]


function renderDocBadge(status: string) {
    switch (status) {
        case 'DRAFT':
            return <Badge variant={'outline'}><PencilIcon/> Piszkozat</Badge>
        case 'ORDER_PENDING':
            return <Badge className={"bg-amber-500"}><HourglassIcon/> Megerősítésre vár</Badge>
        case 'ORDER_CONFIRMED':
            return <><Badge className={"bg-constructive"}><HandshakeIcon/>Megerősítve</Badge><Badge className={"bg-amber-500"}><MailIcon/> Kiküldésre vár</Badge></>
        case 'ORDER_DELIVERED':
            return <Badge className={"bg-constructive"}><CheckIcon/>Kiküldve</Badge>
        case 'ARCHIVED_SUCCESSFUL':
            return <Badge className={'bg-green-500'}><LockClosedIcon/>Sikeresen lezárva</Badge>
        case 'ARCHIVED_CANCELLED':
            return <Badge className={"bg-slate-500"}><LockClosedIcon />Sikertelen lezárva</Badge>
        default:
            return ""
    }
}
export function columns(auth: AuthContextProps): ColumnDef<Document>[] {
    return [
        {
            header: "Név",
            cell: ({row}) => <div className={"flex flex-col"}>
                                <span
                                    className={"font-black"}>{row.original.form.title ?? 'Vállalati Stratégia Tanulmány'}</span>
                <span
                    className={"font-bold text-xs"}>{documentModules.map(module => (row.original.form.theme[module.id] ?? false) ? module.name : null).filter(item => item != null).join(', ')}</span>
                <span
                    className={"font-bold text-xs text-muted-foreground mt-1"}>Létrehozva: {format(row.original.created_at, "yyyy.MM.dd.")}</span>
            </div>
        },
        {
            accessorKey: "company_name",
            header: "Cég",
            cell: ({row}) => (
                <div className={"flex gap-2 items-center"}>
                    <Avatar className="h-8 w-8 rounded bg-black">
                        <AvatarFallback
                            className="rounded-md bg-black text-sidebar-foreground">{row.original.company.name.split(" ").slice(0, -1).splice(0, 2).map(x => x[0])}</AvatarFallback>
                    </Avatar>

                    <div className={"flex flex-col"}>
                        <span className={"font-bold"}>{row.original.company.name}</span>
                        <span className={"font-bold text-muted-foreground"}>{row.original.company.tax_number}</span>
                    </div>
                </div>
            )
        },

        {
          header: "Állapot",
          cell: ({row}) => (
           <div className={"flex flex-col gap-1"}>
               {
                   renderDocBadge(row.original.status)
               }
           </div>
          )
        },

        {
            accessorKey: "updated_at",
            header: "Módosítva",
            cell: ({row}) => {
                const date = new Date(row.getValue('updated_at'))
                return (
                    <div className="lowercase">{date.toLocaleDateString("hu-HU")}</div>
                )
            }
        },
        {
            header: "Műveletek",
            cell: ({row}) => {
                return (
                    <div className="flex gap-2 pointer-events-auto">
                        <Button variant={'ghost'} asChild onClick={event => event.stopPropagation()}>
                            <Link to={`/documents/${row.original.id}/edit`}>
                                <PencilIcon/>
                            </Link>
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant={'ghost'} onClick={(e) => e.stopPropagation()}>
                                    <Printer/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                                side={'bottom'}
                                align="end"
                                sideOffset={4}
                            >
                                <DropdownMenuLabel className="p-0 font-normal">
                                    Nyomtatási mód:
                                </DropdownMenuLabel>
                                <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation()
                                    window.open(`/api/print/${row.original.id}?api_key=` + auth.user?.access_token, "_blank")
                                }}>
                                    <Printer/>
                                    Irodai
                                </DropdownMenuItem>

                                <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation()
                                    window.open(`/api/print/${row.original.id}?press=true&api_key=` + auth.user?.access_token, "_blank")
                                }}>
                                    <Printer/>
                                    Nyomdai
                                </DropdownMenuItem>

                            </DropdownMenuContent>
                        </DropdownMenu>

                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button className={"text-destructive hover:text-red-800"} variant="ghost" onClick={(e) => e.stopPropagation()}><Trash2Icon/></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Biztosan törli a dokumentumot?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Ez a művelet nem vonható vissza. A dokumentum és az ahhoz rögzített adatok
                                        véglegesen el fognak veszni.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel onClick={(e) => {
                                        e.stopPropagation()
                                    }}>Mégse</AlertDialogCancel>
                                    <AlertDialogAction className={"bg-destructive hover:bg-red-700"} onClick={(e) => {
                                        e.stopPropagation()
                                        axios.delete(`/api/documents/${row.original.id}`).then(res => {
                                            if (res.status === 204) {
                                                window.location.reload();
                                            }
                                        })
                                    }}>Törlés</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                    </div>
                )
            }
        }
    ]
}